import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { useParams } from "wouter";
import { useState } from "react";
import { toast } from "sonner";
import {
  Trophy, Users, DollarSign, Clock, Gamepad2, Percent,
  CheckCircle2, XCircle, Loader2, ArrowRight, Star,
  TrendingUp, Shield, Sparkles, ExternalLink,
} from "lucide-react";

export default function Partner() {
  const { token } = useParams<{ token: string }>();
  const [notes, setNotes] = useState("");
  const [reason, setReason] = useState("");
  const [showDeclineForm, setShowDeclineForm] = useState(false);

  const { data, isLoading, error, refetch } = trpc.partner.getOffer.useQuery(
    { token: token || "" },
    { enabled: !!token, retry: false }
  );

  const acceptMutation = trpc.partner.acceptOffer.useMutation({
    onSuccess: () => {
      toast.success("Offer accepted! Welcome to GamePoint.");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const declineMutation = trpc.partner.declineOffer.useMutation({
    onSuccess: () => {
      toast.success("Offer declined. Thank you for your consideration.");
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 text-neon animate-spin mx-auto mb-4" />
          <p className="text-slate-400">Loading your offer...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
        <Card className="max-w-md w-full bg-slate-900/80 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Offer Not Found</h2>
            <p className="text-slate-400">
              This offer link may have expired or is no longer valid. Please contact the GamePoint team for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { offer, prospect, campaign, game, gameTemplate } = data;
  const gameInfo = game || gameTemplate;
  const isResponded = offer.status === "accepted" || offer.status === "declined";

  // Already responded state
  if (isResponded) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
        <Card className="max-w-lg w-full bg-slate-900/80 border-slate-700">
          <CardContent className="pt-8 pb-8 text-center">
            {offer.status === "accepted" ? (
              <>
                <CheckCircle2 className="w-20 h-20 text-emerald-400 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Offer Accepted!</h2>
                <p className="text-slate-400 mb-6">
                  You've accepted this partnership. The GamePoint team will be in touch with next steps to get your game live.
                </p>
                {game?.slug && (
                  <a
                    href={`/game/${game.slug}`}
                    className="inline-flex items-center gap-2 text-neon hover:text-neon/80 transition"
                  >
                    View Your Game <ExternalLink className="w-4 h-4" />
                  </a>
                )}
              </>
            ) : (
              <>
                <XCircle className="w-20 h-20 text-slate-500 mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white mb-2">Offer Declined</h2>
                <p className="text-slate-400">
                  Thank you for your consideration. If you change your mind, feel free to reach out to the GamePoint team.
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
      {/* Hero Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal/10 via-transparent to-emerald-500/10" />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-neon/5 rounded-full blur-3xl" />

        <div className="relative max-w-4xl mx-auto px-4 pt-12 pb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-teal to-emerald-500 flex items-center justify-center">
              <Trophy className="w-5 h-5 text-white" />
            </div>
            <span className="text-lg font-bold text-white tracking-tight">GamePoint</span>
            <Badge variant="outline" className="ml-auto border-neon/30 text-neon text-xs">
              Partnership Offer
            </Badge>
          </div>

          {prospect && (
            <p className="text-slate-400 mb-2">
              Hi <span className="text-white font-medium">{prospect.name}</span>,
            </p>
          )}
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
            You're Invited to Host a Game
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl">
            {campaign?.offerMessage || "We'd love to partner with you to create an exciting competition for your audience. Review the details below and let us know if you're interested."}
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 pb-16">
        {/* Personalized Message */}
        {offer.personalizedMessage && (
          <Card className="bg-slate-900/60 border-slate-700/50 mb-6">
            <CardContent className="pt-6">
              <div className="flex items-start gap-3">
                <Sparkles className="w-5 h-5 text-amber-400 mt-0.5 shrink-0" />
                <div>
                  <p className="text-sm font-medium text-amber-400 mb-1">Personal Message</p>
                  <p className="text-slate-300 leading-relaxed">{offer.personalizedMessage}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Game Details */}
        {gameInfo && (
          <Card className="bg-slate-900/60 border-slate-700/50 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Gamepad2 className="w-5 h-5 text-neon" />
                Game Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <h3 className="text-xl font-bold text-white mb-2">{gameInfo.title}</h3>
              {gameInfo.description && (
                <p className="text-slate-400 mb-6 leading-relaxed">{gameInfo.description}</p>
              )}

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <Gamepad2 className="w-5 h-5 text-neon mx-auto mb-2" />
                  <p className="text-xs text-slate-500 mb-1">Game Type</p>
                  <p className="text-sm font-semibold text-white capitalize">
                    {(game ? game.gameType : gameTemplate?.type) || "Creative"}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <DollarSign className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-500 mb-1">Entry Fee</p>
                  <p className="text-sm font-semibold text-white">
                    ${((gameInfo.entryFee || 0) / 100).toFixed(2)}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <Users className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-500 mb-1">Max Players</p>
                  <p className="text-sm font-semibold text-white">
                    {gameInfo.maxPlayers || 200}
                  </p>
                </div>
                <div className="bg-slate-800/50 rounded-lg p-4 text-center">
                  <Clock className="w-5 h-5 text-amber-400 mx-auto mb-2" />
                  <p className="text-xs text-slate-500 mb-1">Duration</p>
                  <p className="text-sm font-semibold text-white">
                    {gameInfo.duration || "7 days"}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Commission & Revenue */}
        <Card className="bg-slate-900/60 border-slate-700/50 mb-6">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-emerald-400" />
              Your Earnings
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border border-emerald-500/20 rounded-xl p-5 text-center">
                <Percent className="w-8 h-8 text-emerald-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  {gameInfo?.promoterCut || 15}%
                </p>
                <p className="text-sm text-slate-400">Commission Rate</p>
                <p className="text-xs text-slate-500 mt-1">Of all entry fees collected</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500/10 to-blue-500/5 border border-blue-500/20 rounded-xl p-5 text-center">
                <DollarSign className="w-8 h-8 text-blue-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">
                  ${(((gameInfo?.entryFee || 500) / 100) * (gameInfo?.maxPlayers || 200) * ((gameInfo?.promoterCut || 15) / 100)).toFixed(0)}
                </p>
                <p className="text-sm text-slate-400">Potential Earnings</p>
                <p className="text-xs text-slate-500 mt-1">If game fills to capacity</p>
              </div>
              <div className="bg-gradient-to-br from-amber-500/10 to-amber-500/5 border border-amber-500/20 rounded-xl p-5 text-center">
                <Star className="w-8 h-8 text-amber-400 mx-auto mb-2" />
                <p className="text-2xl font-bold text-white">Automatic</p>
                <p className="text-sm text-slate-400">Payout System</p>
                <p className="text-xs text-slate-500 mt-1">Earnings deposited to your wallet</p>
              </div>
            </div>

            <div className="bg-slate-800/30 rounded-lg p-4">
              <h4 className="text-sm font-medium text-white mb-3 flex items-center gap-2">
                <Shield className="w-4 h-4 text-neon" />
                How It Works
              </h4>
              <ol className="space-y-2 text-sm text-slate-400">
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-neon/20 text-neon text-xs flex items-center justify-center shrink-0 mt-0.5">1</span>
                  Accept this offer and we'll set up the game for you
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-neon/20 text-neon text-xs flex items-center justify-center shrink-0 mt-0.5">2</span>
                  Share the game link with your audience using your unique promo code
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-neon/20 text-neon text-xs flex items-center justify-center shrink-0 mt-0.5">3</span>
                  Earn commission on every player who joins through your link
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-5 h-5 rounded-full bg-neon/20 text-neon text-xs flex items-center justify-center shrink-0 mt-0.5">4</span>
                  Earnings are automatically deposited to your GamePoint wallet
                </li>
              </ol>
            </div>
          </CardContent>
        </Card>

        {/* Accept / Decline Actions */}
        <Card className="bg-slate-900/60 border-slate-700/50">
          <CardContent className="pt-6">
            {!showDeclineForm ? (
              <div className="space-y-4">
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    Notes (optional) — share anything you'd like us to know
                  </label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="e.g., I'd like to adjust the game type, or I have questions about..."
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 min-h-[80px]"
                  />
                </div>

                <div className="flex flex-col sm:flex-row gap-3">
                  <Button
                    size="lg"
                    className="flex-1 bg-gradient-to-r from-teal to-emerald-500 hover:from-teal/90 hover:to-emerald-500/90 text-white font-semibold"
                    onClick={() => acceptMutation.mutate({ token: token || "", notes: notes || undefined })}
                    disabled={acceptMutation.isPending}
                  >
                    {acceptMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 mr-2" />
                    )}
                    Accept Partnership
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-slate-600 text-slate-400 hover:text-white hover:border-slate-500"
                    onClick={() => setShowDeclineForm(true)}
                  >
                    Decline
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-white font-medium">Are you sure you want to decline?</p>
                <div>
                  <label className="text-sm text-slate-400 mb-2 block">
                    Reason (optional) — helps us improve future offers
                  </label>
                  <Textarea
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g., Not the right fit for my audience, timing doesn't work..."
                    className="bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-600 min-h-[80px]"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    variant="outline"
                    className="border-slate-600 text-slate-400 hover:text-white"
                    onClick={() => setShowDeclineForm(false)}
                  >
                    Go Back
                  </Button>
                  <Button
                    variant="destructive"
                    onClick={() => declineMutation.mutate({ token: token || "", reason: reason || undefined })}
                    disabled={declineMutation.isPending}
                  >
                    {declineMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <XCircle className="w-4 h-4 mr-2" />
                    )}
                    Confirm Decline
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-sm text-slate-600">
          <p>Powered by GamePoint — The platform for creator-driven competitions</p>
        </div>
      </div>
    </div>
  );
}
